import { useEffect, useMemo, useRef, useState } from "react";
import { Languages, Volume2, Square, Loader2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LANGUAGES, getLang, setGlobalLang, getTtsLocale, getGoogleCode } from "@/lib/lang";

const sha12 = async (text) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 12);
};

const gtxTranslate = async (text, gcode) => {
  const chunks = [];
  for (let i = 0; i < text.length; i += 1500) chunks.push(text.slice(i, i + 1500));
  const out = [];
  for (const chunk of chunks) {
    const r = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${encodeURIComponent(gcode)}&dt=t&q=${encodeURIComponent(chunk)}`
    );
    if (!r.ok) throw new Error("translate failed");
    const data = await r.json();
    out.push((data[0] || []).map((seg) => seg?.[0] || "").join(""));
  }
  return out.join("");
};

export const usePageTranslation = (blocks, cacheKey) => {
  const [lang, setLang] = useState(getLang());
  const [t, setT] = useState({});
  const [busy, setBusy] = useState(false);
  const blocksKey = useMemo(() => JSON.stringify(blocks || {}), [blocks]);

  useEffect(() => {
    const h = () => setLang(getLang());
    window.addEventListener("mp-lang-changed", h);
    return () => window.removeEventListener("mp-lang-changed", h);
  }, []);

  useEffect(() => {
    let alive = true;
    setT({});
    if (lang === "en" || !blocks || !Object.keys(blocks).length) return;
    setBusy(true);
    (async () => {
      const hash = await sha12(blocksKey);
      try {
        const cached = await api.get(`/translations/${cacheKey}/${lang}`, { params: { hash } });
        if (alive) setT(cached.data.blocks || {});
        return;
      } catch {
        /* cache miss — translate in browser */
      }
      try {
        const gcode = getGoogleCode(lang);
        const translated = {};
        const keys = Object.keys(blocks);
        for (let i = 0; i < keys.length; i += 4) {
          const batch = keys.slice(i, i + 4);
          const results = await Promise.all(batch.map((k) => gtxTranslate(String(blocks[k]), gcode)));
          batch.forEach((k, j) => (translated[k] = results[j] || String(blocks[k])));
          if (alive) setT({ ...translated });
        }
        api.post("/translations", { cache_key: cacheKey, lang, hash, blocks: translated }).catch(() => {});
      } catch {
        try {
          const l = LANGUAGES.find((x) => x.code === lang);
          const r = await api.post("/translate", { cache_key: cacheKey, target_lang: `${l.name} (${l.code})`, blocks });
          if (alive) setT(r.data.blocks || {});
        } catch {
          if (alive) toast.error("Translation is temporarily unavailable");
        }
      }
    })().finally(() => alive && setBusy(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, cacheKey, blocksKey]);

  const tr = (key, fallback) => t[key] || fallback;
  return { lang, tr, busy };
};

export const PageToolbar = ({ speakText, busy, lang }) => {
  const [speaking, setSpeaking] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const queueRef = useRef(0);

  useEffect(
    () => () => {
      window.speechSynthesis?.cancel();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    []
  );

  const stop = () => {
    window.speechSynthesis?.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
  };

  const browserSpeak = () => {
    if (!window.speechSynthesis || !speakText) return false;
    const locale = getTtsLocale(lang);
    const chunks = speakText.match(/[^.!?。！？\n]+[.!?。！？]?/g) || [speakText];
    const id = ++queueRef.current;
    setSpeaking(true);
    chunks.forEach((chunk, i) => {
      const u = new SpeechSynthesisUtterance(chunk.trim());
      u.lang = locale;
      u.rate = 0.98;
      if (i === chunks.length - 1) u.onend = () => queueRef.current === id && setSpeaking(false);
      window.speechSynthesis.speak(u);
    });
    return true;
  };

  const listen = async () => {
    if (speaking) return stop();
    if (!speakText) return;
    if (browserSpeak()) return;
    setLoadingAudio(true);
    try {
      const r = await api.post("/tts", { text: speakText.slice(0, 8000) }, { timeout: 120000 });
      const audio = new Audio(`${api.defaults.baseURL}/tts/${r.data.key}.mp3`);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
      setSpeaking(true);
    } catch {
      toast.error("Narration is unavailable right now");
    } finally {
      setLoadingAudio(false);
    }
  };

  return (
    <div data-testid="page-toolbar" className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2 bg-[#fbf8f1] border border-[#d8ccb8] rounded-full pl-3 pr-2 py-1.5">
        <Languages className="w-4 h-4 text-[#7d1a1a]" />
        <select
          data-testid="language-select"
          value={lang}
          onChange={(e) => setGlobalLang(e.target.value)}
          className="bg-transparent text-sm outline-none cursor-pointer max-w-[160px]"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-[#b06810]" />}
      </div>
      <button
        data-testid="listen-button"
        onClick={listen}
        disabled={loadingAudio}
        className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-70 ${
          speaking ? "bg-[#7d1a1a] text-[#f6f1e7]" : "bg-[#1c1512] text-[#f6f1e7] hover:bg-[#7d1a1a]"
        }`}
      >
        {loadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : speaking ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-4 h-4" />}
        {loadingAudio ? "Preparing…" : speaking ? "Stop" : "Listen To ▸"}
      </button>
    </div>
  );
};

export const VerifiedBadge = () => (
  <span data-testid="verified-badge" className="inline-flex items-center gap-0.5 font-mono-label text-[9px] tracking-[0.08em] uppercase text-[#1f4d2e]">
    <BadgeCheck className="w-3.5 h-3.5" /> SocialPulse
  </span>
);

export const UnsplashCredit = ({ photo, className = "" }) => {
  if (!photo) return null;
  return (
    <p className={`font-mono-label text-[10px] tracking-wide text-[#8a7c6d] ${className}`}>
      Photo by{" "}
      <a data-testid="unsplash-photographer-link" href={photo.photographer_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#7d1a1a]">
        {photo.photographer}
      </a>{" "}
      on{" "}
      <a data-testid="unsplash-link" href={photo.unsplash_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#7d1a1a]">
        Unsplash
      </a>
    </p>
  );
};
